import { RotateCcw, Copy, FileText, FileJson, ArrowUp, ArrowDown } from 'lucide-react';
import type { MoscowItem, MoscowCategory } from '../types';

interface ReviewViewProps {
  items: MoscowItem[];
  onMoveItem: (itemId: string, newCategory: MoscowCategory) => void;
  onReset: () => void;
}

export function ReviewView({ items, onMoveItem, onReset }: ReviewViewProps) {
  const categories: MoscowCategory[] = ['Must', 'Should', 'Could', 'Wont'];
  const categoryLabels: Record<MoscowCategory, string> = {
    Must: 'Must Have',
    Should: 'Should Have',
    Could: 'Could Have',
    Wont: "Won't Have",
  };

  const getCategoryColor = (category: MoscowCategory) => {
    switch (category) {
      case 'Must':
        return 'bg-[var(--coral-dim)] border-[var(--coral)] text-[var(--coral)]';
      case 'Should':
        return 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]';
      case 'Could':
        return 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]';
      case 'Wont':
        return 'bg-[var(--surface)] border-[var(--rule)] text-[var(--text-pri)]';
    }
  };

  const handleExport = async (format: 'markdown' | 'json' | 'clipboard') => {
    let content = '';
    let filename = '';
    let mimeType = '';

    switch (format) {
      case 'markdown':
        content = `# MoSCoW Prioritization Results\n\n`;
        content += `Generated: ${new Date().toLocaleString()}\n\n`;
        categories.forEach((category) => {
          const categoryItems = items.filter((item) => item.category === category);
          if (categoryItems.length > 0) {
            content += `## ${categoryLabels[category]}\n\n`;
            categoryItems.forEach((item) => {
              content += `- ${item.name}\n`;
            });
            content += '\n';
          }
        });
        filename = `moscow-${new Date().toISOString().split('T')[0]}.md`;
        mimeType = 'text/markdown';
        break;
      case 'json':
        content = JSON.stringify(items, null, 2);
        filename = `moscow-${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json';
        break;
      case 'clipboard':
        content = categories.map((category) => {
          const categoryItems = items.filter((item) => item.category === category);
          if (categoryItems.length === 0) return '';
          return `${categoryLabels[category]}:\n${categoryItems.map((item) => `  - ${item.name}`).join('\n')}`;
        }).filter(Boolean).join('\n\n');
        await navigator.clipboard.writeText(content);
        alert('Copied to clipboard!');
        return;
    }

    if (format !== 'clipboard') {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 mb-6">
        <h2 className="text-3xl font-bold text-[var(--text-pri)] mb-2">
          MoSCoW Prioritization Review
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          Review your categorized items. You can move items between categories if needed.
        </p>

        {/* Categories */}
        <div className="space-y-6 mb-6">
          {categories.map((category) => {
            const categoryItems = items.filter((item) => item.category === category);
            return (
              <div key={category} className={`p-4  border-2 ${getCategoryColor(category)}`}>
                <h3 className="text-xl font-bold mb-3">
                  {categoryLabels[category]} ({categoryItems.length})
                </h3>
                {categoryItems.length > 0 ? (
                  <div className="space-y-2">
                    {categoryItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-3 bg-[var(--bg-raised)] border border-[var(--rule)]"
                      >
                        <span className="text-[var(--text-pri)]">{item.name}</span>
                        <div className="flex gap-2">
                          {categories
                            .filter((c) => c !== category)
                            .map((newCategory) => (
                              <button
                                key={newCategory}
                                onClick={() => onMoveItem(item.id, newCategory)}
                                className="text-xs px-2 py-1 bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] rounded text-[var(--text-sec)]"
                                title={`Move to ${categoryLabels[newCategory]}`}
                              >
                                {newCategory === 'Must' && <ArrowUp className="h-3 w-3" />}
                                {newCategory === 'Should' && <ArrowUp className="h-3 w-3" />}
                                {newCategory === 'Could' && <ArrowDown className="h-3 w-3" />}
                                {newCategory === 'Wont' && <ArrowDown className="h-3 w-3" />}
                              </button>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm opacity-75 italic">No items in this category</p>
                )}
              </div>
            );
          })}
        </div>

        {/* Export Options */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-3">Export</h3>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handleExport('markdown')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileText className="h-5 w-5" />
              <span className="text-xs">Markdown</span>
            </button>
            <button
              onClick={() => handleExport('json')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileJson className="h-5 w-5" />
              <span className="text-xs">JSON</span>
            </button>
            <button
              onClick={() => handleExport('clipboard')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <Copy className="h-5 w-5" />
              <span className="text-xs">Clipboard</span>
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onReset}
            className="flex-1 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-5 w-5" />
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}


